import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import { disablePublicAnalytics, syncPublicAnalyticsForPage, type PublicAnalyticsConfig } from '@/lib/analytics'

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    if (el) {
      const initialPage = props.initialPage as {
        component: string
        props: {
          analytics?: PublicAnalyticsConfig | null
          current_user?: unknown
        }
      }
      syncPublicAnalyticsForPage(initialPage)
      router.on('before', (event) => {
        const path = new URL(event.detail.visit.url, window.location.origin).pathname
        if (path === '/login' || path.startsWith('/passwords') || path.startsWith('/admin')) {
          disablePublicAnalytics()
        }
      })
      router.on('navigate', (event) => syncPublicAnalyticsForPage(event.detail.page))

      const application = createElement(ErrorBoundary, null, createElement(App, props))
      if (el.hasChildNodes()) {
        hydrateRoot(el, application)
      } else {
        createRoot(el).render(application)
      }
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
